import { usePortalMessage } from "../../i18n/message.js";
import { portalLocale, portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Requests about one person" — the AC-34 half of `data-screen-label="Compliance"` (P14-05).
 *
 * AC-34 is **24 hours**, and the acceptance criterion is a deadline rather than an aspiration.
 * That is the whole reason this panel is its own file: the form is four controls, and the
 * interesting work is rendering where each request stands against a clock.
 *
 * THREE STATES, RENDERED AS THREE DIFFERENT THINGS.
 *
 *   `pending`   — inside the window. Nothing is wrong, so nothing is coloured as if it were:
 *                 a request made a minute ago is not a problem, and a screen that alerted on
 *                 every one of them would teach its reader to ignore the colour by Tuesday.
 *   `overdue`   — outside the window and unfinished. **This is a breach of the promise
 *                 printed on this very screen**, not a slow queue, and it is rendered as one:
 *                 critical tone, the number of hours late, and a sentence that says what has
 *                 been broken. A neutral "in progress" here would be the screen lying about
 *                 the product's own commitment.
 *   `completed` — carries `withinDeadline`, so a request that finished *late* still says so
 *                 after the fact. The record of a missed deadline outlives the miss.
 *
 * QUEUED, NOT RUNNING. The API records a request and queues it (design §15.6.8); the worker
 * that carries it out is plan-003's. So a request inside its window reads "Queued", never
 * "Running" — the 24-hour clock runs from the ask whether or not anything is processing it.
 *
 * NO CLOCK OF ITS OWN. `progress` is computed where the request is, from
 * `judgePersonDataRequest`; this panel reads it. oxlint's `react(purity)` rule forbids
 * `Date.now()` during render, and it is right to: a countdown recomputed on every paint is
 * not more truthful, it is only more jittery. Where the panel needs to say *when* the
 * countdown was taken, it prints the query's own `dataUpdatedAt`.
 *
 * AMENDED 2026-09-25 (customer-portal review 56, the part package G left): the phone example is
 * written in the ACCOUNT's own format — "+30 69…" for a Greek account — read from
 * `readTestCallAvailability`'s `country_calling_code`, the same example the Test console's phone
 * field shows. It used to be a US number, "+1 415 555 0187", for every account.
 *
 * AMENDED 2026-09-26 (customer-portal review item 197): a completed export offers its file —
 * "Download the export" asks the API for a short-lived, audited link, and the row says until when
 * the file is kept, or that the export container's seven-day rule has removed it.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Input, Tag } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { PERSON_DATA_NOTE } from "../../api/governance-view.js";
import { QUERY_KEY } from "../../api/query.js";
import { TEST_CALL_AVAILABILITY_KEY } from "../test-console/test-chat-panel.js";
import { phoneExampleOf } from "../test-console/voice-test-form.js";
import type {
  PersonDataRequestKindView,
  PersonDataRequestProgressView,
  PersonDataRequestRowView,
} from "../../api/gateway.js";

// The 24-hour clock the rest of the console prints ("Today 19:02"), whatever the browser's locale.
const CLOCK = () => new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-GB"), { hour: "2-digit", minute: "2-digit" });

/** Plain English for the per-store counts, which are keyed by the store, not by a label. */

export function hourText(hour: number): string {
  return portalText("finance", "request.hours", { count: hour });
}

/** How long a finished request took. Whole hours, floored, so a one-second run is not "1 hour". */
export function elapsedText(hour: number): string {
  return hour === 0 ? portalText("finance", "m0920") : hourText(hour);
}

export function affectedText(store: string, count: number): string {
  switch (store) {
    case "conversation": return portalText("finance", "request.affected.conversation", { count: count });
    case "recording": return portalText("finance", "request.affected.recording", { count: count });
    case "transcript": return portalText("finance", "request.affected.transcript", { count: count });
    case "order": return portalText("finance", "request.affected.order", { count: count });
    case "survey_result": return portalText("finance", "request.affected.survey_result", { count: count });
    case "caller_detail": return portalText("finance", "request.affected.caller_detail", { count: count });
    case "shop_customer": return portalText("finance", "request.affected.shop_customer", { count: count });
    case "contact": return portalText("finance", "request.affected.contact", { count: count });
    case "do_not_call_entry": return portalText("finance", "request.affected.do_not_call_entry", { count: count });
    default: return `${String(count)} ${store.replaceAll("_", " ")}`;
  }
}

function kindText(kind: PersonDataRequestKindView): string {
  return kind === "export" ? portalText("finance", "m0921") : portalText("finance", "m0922");
}

export interface PersonDataRequestPanelProps {
  readonly row: readonly PersonDataRequestRowView[];
  /** AC-34, in the unit the copy states it in. Printed, never assumed. */
  readonly deadlineHour: number;
  /** When the rows were read. The only clock this panel has, and it is honest about it. */
  readonly readAtMs: number;
}

export function PersonDataRequestPanel({
  row,
  deadlineHour,
  readAtMs,
}: PersonDataRequestPanelProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  // Only for the example's calling code; a read that fails leaves the neutral "+… with your country code".
  const availability = useQuery({
    queryKey: TEST_CALL_AVAILABILITY_KEY,
    queryFn: () => gateway.readTestCallAvailability(),
    staleTime: 30_000,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [message, setMessage] = usePortalMessage();
  const [queued, setQueued] = useState<PersonDataRequestKindView | null>(null);
  // A deletion cannot be undone, so it waits here until it is confirmed.
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const ask = useMutation({
    mutationFn: (kind: PersonDataRequestKindView) =>
      gateway.requestPersonData({ kind, searchTerm: searchTerm.trim() }),
    onSuccess: async (_made, kind) => {
      setSearchTerm("");
      setMessage(null);
      setQueued(kind);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.compliance });
    },
    onError: (error: unknown) => {
      setQueued(null);
      setMessage(() => (apiErrorText(error, portalText("finance", "m0923"))));
    },
  });

  const overdue = row.filter((one) => one.progress.state === "overdue");
  const lateCompletion = row.filter(
    (one) => one.progress.state === "completed" && !one.progress.withinDeadline,
  );

  // The buttons stay enabled, as the design draws them; an empty box is answered with a
  // sentence rather than a request, so nothing is ever asked for about nobody.
  const askFor = (kind: PersonDataRequestKindView): void => {
    if (ask.isPending) return;
    if (searchTerm.trim() === "") {
      setQueued(null);
      setMessage(() => (portalText("finance", "m0924")));
      return;
    }
    if (kind === "delete") {
      setConfirmingDelete(true);
      return;
    }
    ask.mutate(kind);
  };
  // "Nothing has been … yet" is true only while a request is still waiting for the worker.
  const stillQueued = row.some((one) => one.progress.state !== "completed");

  return (
    <div className="cmp-card">
      <p className="cmp-card-kicker cmp-card-kicker-field">{portalText("finance", "m0925")}</p>

      <Field label={portalText("finance", "m0926")}>
        {(control) => (
          <Input
            {...control}
            placeholder={portalText("finance", "m0927", { value1: phoneExampleOf(availability.data?.countryCallingCode ?? null) })}
            title={portalText("finance", "m0928")}
            value={searchTerm}
            onChange={(event) => {
              setSearchTerm(event.currentTarget.value);
              setMessage(null);
            }}
          />
        )}
      </Field>

      <div className="cmp-card-actions cmp-card-actions-tight">
        <Button
          variant="secondary"
          aria-disabled={ask.isPending}
          onClick={() => {
            askFor("export");
          }}
        >{portalText("finance", "m0929")}</Button>
        <Button
          variant="secondary"
          aria-disabled={ask.isPending}
          onClick={() => {
            askFor("delete");
          }}
        >{portalText("finance", "m0930")}</Button>
      </div>

      <p className="cmp-note-tight cmp-note-gap">{PERSON_DATA_NOTE()}</p>
      {row.length === 0 ? null : (
        <p className="cmp-note-tight cmp-note-gap">{portalText("finance", "m0931")}{" "}
          {hourText(deadlineHour)}{portalText("finance", "m0932")}</p>
      )}

      {queued === null || !stillQueued ? null : (
        <p className="cmp-note-tight cmp-note-gap" role="status">
          {queuedText(queued)}
        </p>
      )}

      <Dialog
        open={confirmingDelete}
        title={portalText("finance", "m0933")}
        onClose={() => {
          setConfirmingDelete(false);
        }}
        actions={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmingDelete(false);
              }}
            >{portalText("finance", "m0418")}</Button>
            <Button
              variant="primary"
              disabled={ask.isPending}
              onClick={() => {
                setConfirmingDelete(false);
                ask.mutate("delete");
              }}
            >{portalText("finance", "m0930")}</Button>
          </>
        }
      >
        <p className="c-note">{portalText("finance", "m0934")}{" "}
          <strong>{searchTerm.trim()}</strong>{portalText("finance", "m0935")}</p>
      </Dialog>

      {message === null ? null : <p className="c-note c-note-critical">{message}</p>}

      {overdue.length === 0 ? null : (
        <p className="cmp-breach" role="alert">
          {portalText("finance", "personData.overdue", { count: overdue.length, deadline: hourText(deadlineHour) })}</p>
      )}

      {overdue.length === 0 && lateCompletion.length > 0 ? (
        <p className="cmp-note-tight cmp-note-gap">
          {portalText("finance", "personData.lateCompletion", { count: lateCompletion.length, deadline: hourText(deadlineHour) })}</p>
      ) : null}

      {row.length === 0 ? null : (
        <ul className="cmp-request-list">
          {row.map((one) => (
            <li key={one.personDataRequestId} className="cmp-request">
              <div className="c-row c-row-spaced">
                <span className="cmp-request-term">
                  {kindText(one.kind)} · {one.searchTermText}
                </span>
                <ProgressTag progress={one.progress} />
              </div>

              <p className="cmp-request-meta">{portalText("finance", "m0947")}{one.requestedByName} · {one.requestedText}
              </p>

              <p className="cmp-request-meta">
                {progressText(one.progress, deadlineHour)}
              </p>

              {one.affectedCount === null ? null : (
                <p className="cmp-request-meta">{portalText("finance", "m0948")}{" "}
                  {Object.entries(one.affectedCount)
                    .map(([store, count]) => affectedText(store, count))
                    .join(" · ")}
                </p>
              )}

              {one.exportFile === null ? null : (
                <ExportFileAction personDataRequestId={one.personDataRequestId} exportFile={one.exportFile} />
              )}
            </li>
          ))}
        </ul>
      )}

      {row.length === 0 ? null : (
        <p className="cmp-request-asat">{portalText("finance", "m0949")}{CLOCK().format(new Date(readAtMs))}{portalText("finance", "m0950")}</p>
      )}
    </div>
  );
}

/**
 * A completed export's file (customer-portal review item 197). The link is asked for when the
 * button is pressed, never on render: each one is written to the audit log and lasts minutes.
 */
function ExportFileAction({
  personDataRequestId,
  exportFile,
}: {
  readonly personDataRequestId: string;
  readonly exportFile: NonNullable<PersonDataRequestRowView["exportFile"]>;
}): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const link = useMutation({
    mutationFn: () => gateway.downloadPersonDataExport(personDataRequestId),
    onSuccess: async () => {
      // `person_data_request.downloaded` was just written; the audit log shows it on its next read.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.auditEntry });
    },
    onError: async () => {
      // A file removed since this screen was read: the row should say so, not keep offering it.
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.compliance });
    },
  });

  if (exportFile.state === "removed") {
    return (
      <p className="cmp-request-meta">{portalText("finance", "m0951")}</p>
    );
  }
  return (
    <>
      <div className="cmp-request-actions">
        {link.data === undefined ? (
          <Button
            variant="secondary"
            disabled={link.isPending}
            onClick={() => {
              link.mutate();
            }}
          >
            {link.isPending ? portalText("finance", "m0952") : portalText("finance", "m0953")}
          </Button>
        ) : (
          <a className="btn btn-primary" href={link.data.downloadUrl} download>{portalText("finance", "m0954")}</a>
        )}
      </div>
      <p className="cmp-request-meta">
        {link.data === undefined
          ? portalText("finance", "m0955", { value1: exportFile.untilText })
          : portalText("finance", "m0956", { value1: link.data.expiresAtText })}
      </p>
      {link.isError ? (
        <p className="c-note c-note-critical">
          {apiErrorText(link.error, portalText("finance", "m0957"))}
        </p>
      ) : null}
    </>
  );
}

// No `deadlineHour` parameter: each of the three states carries its own figure — hours
// overdue, hours remaining, or none at all — so the deadline itself is never rendered here.
// The surrounding panel states it once, which is where a reader wants it.
function ProgressTag({
  progress,
}: {
  readonly progress: PersonDataRequestProgressView;
}): ReactNode {
  usePortalTranslation("finance");
  if (progress.state === "overdue") {
    return <Tag status="critical">{portalText("finance", "m0958")}{hourText(progress.overdueHour)}</Tag>;
  }
  if (progress.state === "pending") {
    return <Tag status="muted">{portalText("finance", "m0959")}{hourText(progress.remainingHour)}</Tag>;
  }
  return progress.withinDeadline ? (
    <Tag status="positive">{portalText("finance", "m0960")}{elapsedText(progress.elapsedHour)}</Tag>
  ) : (
    <Tag status="critical">{portalText("finance", "m0960")}{elapsedText(progress.elapsedHour)}{portalText("finance", "m0961")}</Tag>
  );
}

/**
 * What the panel says once the API has accepted a request. The API RECORDS and QUEUES it
 * (`202`, `execution.state = 'queued'`, design §15.6.8); nothing has been exported or deleted
 * yet, so the confirmation says exactly that and nothing more.
 */
export function queuedText(kind: PersonDataRequestKindView): string {
  return (
    portalText("finance", kind === "export" ? "request.queuedExport" : "request.queuedDelete")
  );
}

export function progressText(
  progress: PersonDataRequestProgressView,
  deadlineHour: number,
): string {
  if (progress.state === "overdue") {
    return (
      portalText("finance", "m0963", { value1: hourText(deadlineHour), value2: hourText(progress.overdueHour) })
    );
  }
  if (progress.state === "pending") {
    return (
      portalText("finance", "m0964", { value1: hourText(progress.remainingHour), value2: hourText(deadlineHour) })
    );
  }
  return progress.withinDeadline
    ? portalText("finance", "m0965", { value1: elapsedText(progress.elapsedHour), value2: hourText(deadlineHour) })
    : portalText("finance", "m0966", { value1: elapsedText(progress.elapsedHour), value2: hourText(deadlineHour) });
}
